/* eslint-disable react-refresh/only-export-components -- provider + hook
   colocated by convention (matches AuthContext / ViewContext). */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * The hospital an amtc_admin is currently managing. A hospital_admin is scoped
 * to one hospital and ignores this; an amtc_admin oversees all 10 and picks one
 * (via the header `HospitalPicker`), shared by the Home and Beds pages. The
 * choice persists in localStorage so it survives reloads.
 *
 * The default value (null selection, no-op setter) lets components that read
 * the selection render without a Provider — handy for hospital_admin-only tests.
 */
const STORAGE_KEY = 'medairbnb.admin.hospital'

type SelectedHospital = {
  hospitalId: string | null
  setHospitalId: (id: string | null) => void
}

const SelectedHospitalContext = createContext<SelectedHospital>({
  hospitalId: null,
  setHospitalId: () => {},
})

export function SelectedHospitalProvider({
  children,
}: {
  children: ReactNode
}) {
  const [hospitalId, setId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })

  const setHospitalId = useCallback((id: string | null) => {
    setId(id)
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id)
      else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore — selection just won't persist
    }
  }, [])

  const value = useMemo(
    () => ({ hospitalId, setHospitalId }),
    [hospitalId, setHospitalId],
  )

  return (
    <SelectedHospitalContext.Provider value={value}>
      {children}
    </SelectedHospitalContext.Provider>
  )
}

export function useSelectedHospital(): SelectedHospital {
  return useContext(SelectedHospitalContext)
}
